/**
 * @sunday/util: the small helpers every Sunday widget shares. One copy of each, nowhere else.
 *
 *   esc(s)              text → safe HTML text (& < > " ')
 *   money(cents)        store currency, no paise: ₹25,999 (symbol and number style from the store,
 *                       printed once by snippets/sunday-head.liquid as data-sunday-money)
 *   emi(cents, months)  the monthly amount at 0%, rounded up to whole rupees (in cents)
 *   shade(colour, k)    a mattress colour made darker (k < 0) or lighter (k > 0), as CSS color-mix
 *   calm()              true when the visitor asked for reduced motion
 *   fill(text, vars)    '[name]' and '{name}' placeholders → values
 *   play(v), prep(v)    start a clip the iPhone way; a play button over the still when the phone refuses
 */
import { t as tWord } from '@sunday/text';

const CFG = (() => {
  try {
    return JSON.parse(document.querySelector('script[data-sunday-money]').textContent) || {};
  } catch (e) {
    return {};
  }
})();
const SYMBOL = CFG.symbol ?? '';
const NUM = (() => {
  try {
    return new Intl.NumberFormat(CFG.locale || 'en-IN', { maximumFractionDigits: 0 });
  } catch (e) {
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
  }
})();

const ENT = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ENT[c]);

export const money = (cents) => SYMBOL + NUM.format(Math.round((Number(cents) || 0) / 100));

export const emi = (cents, months) => Math.ceil((Number(cents) || 0) / 100 / Math.max(1, Number(months) || 1)) * 100;

/** k from -1 (towards black) to 1 (towards the card colour); 0 returns the colour. */
export function shade(colour, k = 0) {
  if (!k) return colour;
  const to = k < 0 ? 'var(--sd-art-black, #232019)' : 'var(--sd-card, #fff)';
  const keep = Math.round((1 - Math.min(1, Math.abs(k))) * 100);
  return `color-mix(in srgb, ${colour} ${keep}%, ${to})`;
}

/* 21 Oct (AR): delivery dates read "Tue 27/10": the weekday in the page's language (the browser names it, so no
   day names live in code), then day/month as two-digit numbers. Used by the delivery widget, Help me choose and the cart. */
const DLANG = (typeof document !== 'undefined' && document.documentElement.lang || 'en').toLowerCase();
const WDAY = new Intl.DateTimeFormat(DLANG.startsWith('en') ? 'en-US' : `${DLANG}-IN`, { weekday: 'short' });
const two = (n) => String(n).padStart(2, '0');
/** [weekday, 'dd/mm'] so a narrow box can drop the weekday. */
export const dayParts = (d) => [WDAY.format(d), `${two(d.getDate())}/${two(d.getMonth() + 1)}`];
/** 'Tue 27/10' */
export const dayStr = (d) => dayParts(d).filter(Boolean).join(' ');

const RM = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
/* 24 Oct (AR go): story animations and clips play whatever the reduced-motion setting says (iPhones with Reduce
   Motion on showed only the first and last frame). calm() stays for callers but is always false; calmScroll() is the
   one place the setting still counts: smooth scrolling. */
export const calm = () => false;
export const calmScroll = () => !!(RM && RM.matches);

export const fill = (text, vars = {}) =>
  String(text ?? '').replace(/\[(\w+)\]|\{(\w+)\}/g, (m, a, b) => {
    const k = a || b;
    return k in vars ? String(vars[k]) : m;
  });

/* A tiny tick in the hand (24 Oct, AR: Press to feel it, Turn to see inside). Android vibrates at the exact moment.
   iPhone Safari has no vibrate; toggling a switch-style checkbox gives its system tick, but only inside a tap and
   only on Safari builds with switch checkboxes (iOS 17.4 to 26.4; blocked in 26.5), so it is tried only there.
   Silent everywhere else. */
export const CAN_VIB = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
export const CAN_SW = !CAN_VIB && typeof HTMLInputElement !== 'undefined' && 'switch' in HTMLInputElement.prototype;
let hapEl;
export function tick(ms = 10) {
  try {
    if (CAN_VIB) {
      navigator.vibrate(ms);
      return;
    }
    if (!CAN_SW) return;
    if (!hapEl) {
      hapEl = document.createElement('label');
      hapEl.setAttribute('aria-hidden', 'true');
      hapEl.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
      const i = document.createElement('input');
      i.type = 'checkbox';
      i.setAttribute('switch', '');
      i.tabIndex = -1;
      hapEl.appendChild(i);
      document.body.appendChild(hapEl);
    }
    hapEl.click();
  } catch (e) {}
}

/* ---------- clips (30 Oct, AR: on his iPhone the clips showed a white box, or a still that never played) ----------
   Every Sunday clip starts through play(v). Before anything loads: muted and inline (attribute and property: iPhone
   reads both) and the still from data-poster. Then play(). When the phone refuses (Low Power Mode refuses every clip
   that starts by itself) or nothing has started after 6 s, a round play button sits over the still and a tap plays it.
   A clip inside a link or button (a tap there opens something) keeps that tap and shows its still only. */
export function prep(v) {
  if (!v) return v;
  if (!v.controls || v.hasAttribute('muted')) {
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
  }
  v.playsInline = true;
  v.setAttribute('playsinline', '');
  v.setAttribute('webkit-playsinline', '');
  if (!v.getAttribute('poster') && v.dataset.poster) v.poster = v.dataset.poster;
  return v;
}
let tapCss = false;
const hideTap = (v) => { if (v.__sdTap) v.__sdTap.hidden = true; };
function askTap(v) {
  const box = v.parentElement;
  if (!box || box.closest('button, a') || !v.isConnected) return;
  if (!v.__sdTap) {
    if (!tapCss) {
      tapCss = true;
      const st = document.createElement('style');
      st.textContent = '.sd-vtap{position:absolute;left:50%;top:50%;z-index:3;display:grid;place-items:center;width:60px;height:60px;margin:-30px 0 0 -30px;padding:0;border:0;border-radius:99px;background:rgba(255,255,255,.94);color:var(--sd-navy,#2a3341);box-shadow:0 6px 18px rgba(0,0,0,.22);cursor:pointer;-webkit-tap-highlight-color:transparent}.sd-vtap svg{width:20px;height:20px;margin-left:4px}.sd-vtap-s{width:44px;height:44px;margin:-22px 0 0 -22px}.sd-vtap-s svg{width:15px;height:15px;margin-left:3px}.sd-vtap[hidden]{display:none}.sd-vtap:focus-visible{outline:3px solid var(--sd-navy,#2a3341);outline-offset:3px}';
      document.head.appendChild(st);
    }
    if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
    const b = document.createElement('button');
    b.type = 'button';
    b.className = box.clientWidth < 220 ? 'sd-vtap sd-vtap-s' : 'sd-vtap';
    b.setAttribute('aria-label', tWord('common.play'));
    b.innerHTML = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 1v10l9-5z" fill="currentColor"/></svg>';
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      b.hidden = true;
      prep(v);
      const p = v.play(); /* inside the tap, so the phone allows it */
      if (p && p.catch) p.catch(() => (b.hidden = false));
    });
    ['pointerdown', 'pointerup'].forEach((n) => b.addEventListener(n, (e) => e.stopPropagation()));
    v.addEventListener('playing', () => hideTap(v));
    v.__sdTap = b;
    v.after(b);
  }
  v.__sdTap.hidden = false;
}
/** Plays a clip; resolves true when it plays, false when refused (its play button then shows). */
export function play(v) {
  if (!v) return Promise.resolve(false);
  prep(v);
  let p;
  try { p = v.play(); } catch (e) { p = Promise.reject(e); }
  if (!p || !p.then) return Promise.resolve(!v.paused);
  let done = false;
  clearTimeout(v.__sdWait);
  v.__sdWait = setTimeout(() => { if (!done && !v.paused && v.readyState < 3) askTap(v); }, 6000);
  return p.then(
    () => { done = true; clearTimeout(v.__sdWait); hideTap(v); return true; },
    (e) => { done = true; clearTimeout(v.__sdWait); if (e && e.name === 'NotAllowedError') askTap(v); return false; }
  );
}
